/**
 * components/admin/DeliveryPayoutLedger.jsx · v2.7.58+E37 · Section 5
 * ═══════════════════════════════════════════════════════════════════════
 * Admin surface for the delivery payout ledger, auto-populated when a
 * driver marks a manifest DELIVERED (see driver_mark_delivered() in
 * routers/deliveries.py). Ops can filter by status/driver, review the
 * gross / tip / base split, and manually flip POSTED → PAID_OUT once the
 * physical transfer to the driver has been made.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Wallet, RefreshCw, CheckCircle2, Clock, Loader2, DollarSign,
  User as UserIcon, Package as PackageIcon,
} from "lucide-react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";

export default function DeliveryPayoutLedger() {
  const [items, setItems]   = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoad]  = useState(true);
  const [status, setStatus] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [settleModal, setModal] = useState(null); // { id, gross, driver_name }
  const [ref, setRef] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const q = { limit: 200 };
      if (status !== "all") q.payout_status = status;
      const r = await adminApi.get("/deliveries/admin/payouts", { params: q });
      setItems(r.data?.items || []);
      setTotals(r.data?.totals || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load payout ledger");
    } finally { setLoad(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const openSettle = (row) => {
    setModal(row);
    setRef("");
    setNote("");
  };

  const confirmSettle = async () => {
    if (!settleModal) return;
    setBusyId(settleModal.id);
    try {
      await adminApi.post(`/deliveries/admin/payouts/${settleModal.id}/settle`, {
        ref: ref.trim() || null,
        note: note.trim() || null,
      });
      toast.success("Payout marked as PAID_OUT");
      setModal(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not settle payout");
    } finally { setBusyId(null); }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4 space-y-3" data-testid="delivery-payout-ledger">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-emerald-400/15 border border-emerald-400/40 flex items-center justify-center">
            <Wallet className="h-5 w-5 text-emerald-300"/>
          </div>
          <div>
            <div className="text-base font-bold text-zinc-100">Delivery Payout Ledger</div>
            <div className="text-[11px] text-zinc-500">Auto-posted when a driver closes a manifest. Flip POSTED → PAID_OUT once the physical transfer is made.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 bg-zinc-950 border-zinc-700 text-xs" data-testid="payout-status-filter">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="POSTED">POSTED</SelectItem>
              <SelectItem value="PAID_OUT">PAID_OUT</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={load}
            variant="outline"
            size="sm"
            className="bg-zinc-950 border-zinc-700 text-xs"
            data-testid="payout-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1"/>Refresh
          </Button>
        </div>
      </div>

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="payout-totals">
          <TotalPill icon={PackageIcon} label="Rows"    value={totals.count} accent="text-zinc-100"/>
          <TotalPill icon={Clock}        label="POSTED"  value={totals.posted_count} accent="text-amber-300"/>
          <TotalPill icon={CheckCircle2} label="PAID_OUT" value={totals.paid_count} accent="text-emerald-300"/>
          <TotalPill icon={DollarSign}   label="Gross"    value={`$${(totals.gross_total || 0).toFixed(2)}`} accent="text-emerald-300"/>
          <TotalPill icon={DollarSign}   label="Tips"     value={`$${(totals.tip_total || 0).toFixed(2)}`} accent="text-emerald-300"/>
        </div>
      )}

      {loading && (
        <div className="py-6 text-center text-zinc-500 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-1"/>Loading ledger…</div>
      )}

      {!loading && items.length === 0 && (
        <div className="py-8 text-center text-zinc-500 text-xs" data-testid="payout-empty">
          No payouts on record for the selected filter yet.
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="payout-table">
            <thead className="bg-zinc-950 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Driver</th>
                <th className="text-left px-3 py-2">Delivery</th>
                <th className="text-right px-3 py-2">Gross</th>
                <th className="text-right px-3 py-2">Tip</th>
                <th className="text-right px-3 py-2">Stops</th>
                <th className="text-left px-3 py-2">Posted</th>
                <th className="text-left px-3 py-2">Paid Out</th>
                <th className="text-right px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-zinc-800" data-testid={`payout-row-${p.id}`}>
                  <td className="px-3 py-2">
                    <div className="flex flex-col items-start gap-0.5">
                      <span className={`text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded ${
                        p.payout_status === "PAID_OUT"
                          ? "bg-emerald-400 text-zinc-950"
                          : "bg-amber-400 text-zinc-950"
                      }`}>{p.payout_status}</span>
                      {/* v2.7.65+E51 · manual-closeout schema flag */}
                      {(p.source_type === "MANUAL_OVERRIDE" || p.payout_source === "MANUAL_OVERRIDE") && (
                        <span className="text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded border border-violet-400/60 bg-violet-500/20 text-violet-300"
                              title={p.manual_close?.reason ? `Manual closeout · ${p.manual_close.reason}` : "Manual closeout by admin"}
                              data-testid={`payout-row-${p.id}-manual-tag`}>
                          MANUAL
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-zinc-200"><span className="flex items-center gap-1"><UserIcon className="h-3 w-3 text-zinc-500"/>{p.driver_name || p.driver_email || <span className="text-zinc-600">unassigned</span>}</span></td>
                  <td className="px-3 py-2 text-zinc-400 font-mono">#{(p.delivery_id || "").slice(0, 8)}</td>
                  <td className="px-3 py-2 text-right font-bold text-emerald-300">${(p.gross || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right text-emerald-300">${(p.tip || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right text-zinc-300">{p.stops_count}</td>
                  <td className="px-3 py-2 text-zinc-400">{p.posted_at ? new Date(p.posted_at).toLocaleString() : "—"}</td>
                  <td className="px-3 py-2 text-zinc-400">
                    {p.paid_out_at
                      ? <span title={p.paid_out_ref || ""}>{new Date(p.paid_out_at).toLocaleDateString()}</span>
                      : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {p.payout_status === "POSTED"
                      ? (
                        <Button
                          onClick={() => openSettle(p)}
                          disabled={busyId === p.id}
                          size="sm"
                          className="bg-emerald-400 hover:bg-emerald-300 text-zinc-950 font-bold h-7 text-[10px] px-2"
                          data-testid={`settle-btn-${p.id}`}
                        >
                          Settle
                        </Button>
                      ) : (
                        <span className="text-[10px] text-zinc-500">
                          {p.paid_out_ref ? `Ref · ${p.paid_out_ref}` : "settled"}
                        </span>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {settleModal && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setModal(null)}
          data-testid="settle-modal-overlay"
        >
          <Card
            className="bg-zinc-900 border-zinc-700 p-5 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            data-testid="settle-modal"
          >
            <div className="text-base font-bold text-zinc-100 mb-1">Settle Payout</div>
            <div className="text-xs text-zinc-400 mb-3">
              Confirm the physical transfer of <span className="font-bold text-emerald-300">${(settleModal.gross || 0).toFixed(2)}</span> to {settleModal.driver_name || settleModal.driver_email}.
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Reference (Bank / Cash-App / etc.)</Label>
                <Input
                  value={ref}
                  onChange={(e) => setRef(e.target.value)}
                  placeholder="e.g. CASHAPP-TX-12345"
                  className="bg-zinc-950 border-zinc-700 mt-1 text-sm"
                  data-testid="settle-ref"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Note (Optional)</Label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  placeholder="Batch #, memo, etc."
                  className="bg-zinc-950 border-zinc-700 mt-1 text-xs"
                  data-testid="settle-note"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setModal(null)} className="bg-zinc-950 border-zinc-700" data-testid="settle-cancel">Cancel</Button>
              <Button
                onClick={confirmSettle}
                disabled={busyId === settleModal.id}
                className="bg-emerald-400 hover:bg-emerald-300 text-zinc-950 font-extrabold"
                data-testid="settle-confirm"
              >
                {busyId === settleModal.id
                  ? <><Loader2 className="h-4 w-4 animate-spin mr-1"/>Settling…</>
                  : <><CheckCircle2 className="h-4 w-4 mr-1"/>Mark Paid Out</>}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </Card>
  );
}

function TotalPill({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded p-2.5">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
        <Icon className="h-3 w-3"/>{label}
      </div>
      <div className={`text-base font-extrabold mt-0.5 ${accent}`}>{value}</div>
    </div>
  );
}
